import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, UserPlus, UserCheck, Users, Sparkles, Camera, Scissors, Music } from "lucide-react";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { toast } from "sonner";

/**
 * PlyxDiscover — `/plyx/discover`
 *
 * Find registered Plyx members by handle or display name. Tapping a row
 * jumps to their profile (`/plyx/u/:handle`) where the existing follow
 * button lives, but the Follow / Following toggle is also surfaced
 * inline so members can build their network without leaving the search
 * results — the single biggest hurdle in growing a social graph.
 */
const PlyxDiscover = () => {
  const { user } = usePlyxAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = q.trim();
    if (!t) { setResults([]); return undefined; }
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const r = await api.get(`/plyx/users/search?q=${encodeURIComponent(t)}&limit=20`);
        setResults(r.data?.users || []);
      } catch {
        setResults([]);
      } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(id);
  }, [q]);

  const toggleFollow = async (u) => {
    try {
      if (u.is_following) {
        await api.delete(`/plyx/u/${u.handle}/follow`);
        u.is_following = false;
        u.follower_count = Math.max(0, (u.follower_count || 1) - 1);
        toast.message(`Unfollowed @${u.handle}`);
      } else {
        await api.post(`/plyx/u/${u.handle}/follow`);
        u.is_following = true;
        u.follower_count = (u.follower_count || 0) + 1;
        toast.success(`Following @${u.handle}`);
      }
      setResults((prev) => [...prev]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update follow");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pb-24" data-testid="plyx-discover-page">
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <Link
          to="/plyx/millions"
          data-testid="discover-millions-card"
          className="block rounded-2xl p-4 mb-4 bg-gradient-to-r from-[#FFB42D] via-[#FF2D7A] to-[#7b2dff] text-black"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-extrabold text-lg leading-tight">Plyx Millions 💰</div>
              <div className="text-xs font-semibold opacity-80 mt-0.5">Climb the trivia ladder · duel friends for coin pots</div>
            </div>
            <span className="text-2xl">🎯</span>
          </div>
        </Link>
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-amber-300" />
          <h1 className="text-2xl font-black tracking-tight">Find members</h1>
        </div>
        <p className="text-sm text-white/50 mb-5">
          Search every registered Plyx member by their @handle or name. Tap
          Follow to get notified the moment they go live.
        </p>

        {/* iter-118: Creator tools quick-access banner */}
        <div className="mb-5 rounded-2xl bg-gradient-to-br from-fuchsia-600/40 via-amber-500/30 to-cyan-500/40 p-[1px]" data-testid="plyx-discover-ailab-banner">
          <div className="rounded-2xl bg-[#0a0a0f] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-amber-300"/>
              <div className="font-bold text-sm">Make better videos faster</div>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              <Link to="/plyx/cam" className="text-center bg-white/5 hover:bg-white/10 rounded-xl p-2 transition" data-testid="plyx-discover-go-cam">
                <Camera className="w-4 h-4 mx-auto mb-0.5 text-pink-300"/>
                <div className="text-[10px] font-bold">Cam</div>
                <div className="text-[9px] text-white/40">AR filters</div>
              </Link>
              <Link to="/plyx/studio" className="text-center bg-white/5 hover:bg-white/10 rounded-xl p-2 transition" data-testid="plyx-discover-go-studio">
                <Scissors className="w-4 h-4 mx-auto mb-0.5 text-cyan-300"/>
                <div className="text-[10px] font-bold">Studio</div>
                <div className="text-[9px] text-white/40">Trim · music</div>
              </Link>
              <Link to="/plyx/ai-lab" className="text-center bg-white/5 hover:bg-white/10 rounded-xl p-2 transition" data-testid="plyx-discover-go-ailab">
                <Sparkles className="w-4 h-4 mx-auto mb-0.5 text-amber-300"/>
                <div className="text-[10px] font-bold">AI Lab</div>
                <div className="text-[9px] text-white/40">Mood · trends</div>
              </Link>
              <Link to="/plyx/upload" className="text-center bg-white/5 hover:bg-white/10 rounded-xl p-2 transition" data-testid="plyx-discover-go-upload">
                <Music className="w-4 h-4 mx-auto mb-0.5 text-white/80"/>
                <div className="text-[10px] font-bold">Upload</div>
                <div className="text-[9px] text-white/40">+ sound lib</div>
              </Link>
            </div>
          </div>
        </div>

        <label className="relative block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search @handle or name…"
            data-testid="plyx-discover-search"
            autoFocus
            className="w-full rounded-2xl bg-white/5 border border-white/10 pl-10 pr-3 py-3 text-base text-white placeholder-white/30 focus:outline-none focus:border-amber-300"
          />
        </label>

        {loading && q.trim() && (
          <p className="text-white/40 text-xs mt-4">Searching…</p>
        )}

        {!loading && q.trim() && results.length === 0 && (
          <p className="text-white/40 text-sm mt-6 text-center">
            No one found for &ldquo;{q}&rdquo;.
          </p>
        )}

        <div className="mt-4 space-y-2">
          {results.map((u) => (
            <div
              key={u.plyx_user_id}
              data-testid={`plyx-discover-result-${u.handle}`}
              className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/5 hover:bg-white/[0.06] transition"
            >
              <Link to={`/plyx/u/${u.handle}`} className="shrink-0">
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-300 to-pink-500" />
                )}
              </Link>
              <Link to={`/plyx/u/${u.handle}`} className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{u.display_name || `@${u.handle}`}</p>
                <p className="text-xs text-white/50 truncate">
                  @{u.handle} · {u.follower_count || 0} followers
                </p>
              </Link>
              {user && user.handle !== u.handle && (
                <button
                  type="button"
                  onClick={() => toggleFollow(u)}
                  data-testid={`plyx-discover-follow-${u.handle}`}
                  className={
                    "shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition "
                    + (u.is_following
                      ? "bg-white/10 text-white border border-white/15"
                      : "bg-amber-300 text-black hover:bg-amber-200")
                  }
                >
                  {u.is_following ? (
                    <><UserCheck className="w-3.5 h-3.5" /> Following</>
                  ) : (
                    <><UserPlus className="w-3.5 h-3.5" /> Follow</>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>

        {!q.trim() && (
          <div className="mt-10 text-center text-white/40 text-sm">
            <p>Start typing a name or @handle above to find members.</p>
            <p className="mt-1 text-xs">Tip: even a single letter narrows it down.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlyxDiscover;
